// TUT-294b — o documento de processo no portal: o lado do escritório (a seção
// "No portal" da prévia) e o lado do portal (os documentos dos processos, o
// para protocolo, o enviar ao processo e as pastas).
import {
  demoCompartilharDocumento,
  demoDocumentoNoPortal,
  demoMarcarParaProtocolo,
  demoPortalCriarPasta,
  demoPortalDocumentoAccessLink,
  demoPortalDocumentos,
  demoPortalEnviarAoProcesso,
  demoPortalEnviarComprovante,
  demoPortalExcluirPasta,
  demoPortalMoverParaPasta,
  demoPortalPastas,
  demoPortalProtocolar,
  demoPortalRenomearPasta,
  demoRetirarDoProtocolo,
  demoRevogarCompartilhamento,
} from "../demo/documentos-no-portal";
import type {
  CompartilhamentoDeDocumento,
  CompartilharDocumentoInput,
  DocumentoNoPortalDoEscritorio,
  DocumentosDoPortalResponse,
  PastaDoPortal,
  PastasDoPortalResponse,
  ProtocoloDeDocumento,
  ResultadoDoProtocoloNoPortal,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

/** No demo, a recusa do servidor vira a promessa rejeitada com a mesma frase. */
function noDemo<T>(trabalho: () => T): Promise<T> {
  try {
    return Promise.resolve(trabalho());
  } catch (erro) {
    return Promise.reject(erro instanceof Error ? erro : new Error(String(erro)));
  }
}

const id = encodeURIComponent;

export const apiDosDocumentosNoPortal = {
  /* ------------------------------ escritório ------------------------------ */
  documentoNoPortal(documentId: string, processId: string): Promise<DocumentoNoPortalDoEscritorio> {
    if (useDemoApi) return noDemo(() => demoDocumentoNoPortal(documentId, processId));
    return request(`/documents/${id(documentId)}/portal?processId=${id(processId)}`);
  },
  compartilharDocumento(
    documentId: string,
    input: CompartilharDocumentoInput,
  ): Promise<CompartilhamentoDeDocumento> {
    if (useDemoApi) return noDemo(() => demoCompartilharDocumento(documentId, input));
    return request(`/documents/${id(documentId)}/portal/compartilhamentos`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  revogarCompartilhamento(shareId: string): Promise<CompartilhamentoDeDocumento> {
    if (useDemoApi) return noDemo(() => demoRevogarCompartilhamento(shareId));
    return request(`/document-shares/${id(shareId)}/revoke`, { method: "POST" });
  },
  marcarParaProtocolo(
    documentId: string,
    input: { versionId: string; processId: string; recipientUserIds: string[] },
  ): Promise<ProtocoloDeDocumento> {
    if (useDemoApi) return noDemo(() => demoMarcarParaProtocolo(documentId, input));
    return request(`/documents/${id(documentId)}/portal/protocolos`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  retirarDoProtocolo(filingId: string): Promise<ProtocoloDeDocumento> {
    if (useDemoApi) return noDemo(() => demoRetirarDoProtocolo(filingId));
    return request(`/document-filings/${id(filingId)}/withdraw`, { method: "POST" });
  },

  /* -------------------------------- portal -------------------------------- */
  /**
   * Rotas `@Portal()`: o recorte (o alcance de hoje, a revogação, o prazo, o
   * sigilo) é do servidor; a tela não refiltra.
   */
  portalDocumentos(): Promise<DocumentosDoPortalResponse> {
    if (useDemoApi) return noDemo(() => demoPortalDocumentos());
    return request("/portal/documentos");
  },
  portalDocumentoAccessLink(
    documentId: string,
    versionId: string,
    mode: "download" | "inline",
  ): Promise<{ url: string; expiresInSeconds: number }> {
    if (useDemoApi) return noDemo(() => demoPortalDocumentoAccessLink());
    const sufixo = mode === "inline" ? "?modo=inline" : "";
    return request(`/portal/documentos/${id(documentId)}/versions/${id(versionId)}/download${sufixo}`);
  },
  portalProtocolar(
    filingId: string,
    input: { filedOn: string; comprovanteId?: string },
  ): Promise<ResultadoDoProtocoloNoPortal> {
    if (useDemoApi) return noDemo(() => demoPortalProtocolar(filingId, input));
    return request(`/portal/protocolos/${id(filingId)}/protocolar`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  portalEnviarComprovante(filingId: string, documentId: string): Promise<ResultadoDoProtocoloNoPortal> {
    if (useDemoApi) return noDemo(() => demoPortalEnviarComprovante(filingId, documentId));
    return request(`/portal/protocolos/${id(filingId)}/comprovante`, {
      method: "POST",
      body: JSON.stringify({ documentId }),
    });
  },
  portalEnviarAoProcesso(
    processId: string,
    documentId: string,
  ): Promise<{ documentId: string; processId: string }> {
    if (useDemoApi) return noDemo(() => demoPortalEnviarAoProcesso(processId, documentId));
    return request(`/portal/processos/${id(processId)}/documentos`, {
      method: "POST",
      body: JSON.stringify({ documentId }),
    });
  },
  portalPastas(): Promise<PastasDoPortalResponse> {
    if (useDemoApi) return noDemo(() => demoPortalPastas());
    return request("/portal/pastas");
  },
  portalCriarPasta(nome: string): Promise<PastaDoPortal> {
    if (useDemoApi) return noDemo(() => demoPortalCriarPasta(nome));
    return request("/portal/pastas", { method: "POST", body: JSON.stringify({ nome }) });
  },
  portalRenomearPasta(folderId: string, nome: string): Promise<PastaDoPortal> {
    if (useDemoApi) return noDemo(() => demoPortalRenomearPasta(folderId, nome));
    return request(`/portal/pastas/${id(folderId)}`, { method: "PATCH", body: JSON.stringify({ nome }) });
  },
  portalExcluirPasta(folderId: string): Promise<{ ok: true }> {
    if (useDemoApi) return noDemo(() => demoPortalExcluirPasta(folderId));
    return request(`/portal/pastas/${id(folderId)}`, { method: "DELETE" });
  },
  portalMoverParaPasta(
    documentId: string,
    folderId: string | null,
  ): Promise<{ documentId: string; folderId: string | null }> {
    if (useDemoApi) return noDemo(() => demoPortalMoverParaPasta(documentId, folderId));
    return request(`/portal/pastas/itens/${id(documentId)}`, {
      method: "PUT",
      body: JSON.stringify({ folderId }),
    });
  },
};
